import React, { useState, useEffect, useRef, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { X, Save, AlertTriangle, DownloadCloud, Loader, Trash2, Info, Map as MapIcon, MapPinOff, Copy, Check, Download, Search, WifiOff, XCircle, Camera, FileText, Eye, MoreVertical, ChevronDown, ChevronUp, ArrowLeft, Image as ImageIcon, PencilRuler, Zap, Cable, Trees, Users, Wheat } from 'lucide-react';
import { reverseGeocode, scrapeCpflUrl } from '../../services/apiService';
import 'leaflet/dist/leaflet.css';

import StyledFileInput from '../common/StyledFileInput';
import ConfirmDialog from '../common/ConfirmDialog';
import PDFViewerModal from './PDFViewerModal';
import { generateTradeReportPDF } from '../../utils/pdfGenerator';
import { useOzmapData } from '../../hooks/useOzmapData';
import { isPointInPolygon } from '../../utils/geolocationUtils';
import { parseDMSCoordinates, parseDecimalCoordinates } from '../../utils/coordinateUtils';
import { logActivity } from '../../services/activityLogService';
import OzmapDisplay from '../common/OzmapDisplay';
// Importa o logotipo da empresa
import logoConnect10 from '../../assets/logo-connect10.png';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

// --- FUNÇÃO AUXILIAR PARA FORMATAR NOME DO SETOR ---
const formatSectorName = (name) => {
    if (!name) return '';

    let processedName = name.trim();
    let macroRegion = null;
    let sectorNumber = null;

    // 1. Detect Macro-Region (T followed by number)
    const macroMatch = processedName.match(/^T\s*-?\s*(\d+)/i);
    if (macroMatch) {
        macroRegion = `T${macroMatch[1]}`;
    }

    // 2. Detect Sector Number
    // Priority 1: Look for explicit 'S' marker (e.g., S103, S 103, s103)
    const sMatch = processedName.match(/\bS\s*(\d+[A-Za-z]*)\b/i);
    const sConnectedMatch = processedName.match(/S(\d+[A-Za-z]*)/i);

    if (sMatch) {
        sectorNumber = sMatch[1];
    } else if (sConnectedMatch) {
        sectorNumber = sConnectedMatch[1];
    }

    // Priority 2: If no explicit 'S' found, fall back to split strategy or finding second number
    if (!sectorNumber) {
        if (macroRegion) {
             const remainder = processedName.replace(/^T\s*-?\s*\d+\s*/i, '');
             const nextNum = remainder.match(/(\d+[A-Za-z]*)/);
             if (nextNum) {
                 sectorNumber = nextNum[1];
             }
        } else {
            const parts = processedName.split('-');
            if (parts.length > 1) {
                const lastPart = parts[parts.length - 1];
                const lastNum = lastPart.match(/(\d+[A-Za-z]*)/);
                if (lastNum) {
                    sectorNumber = lastNum[1];
                }
            } else {
                const num = processedName.match(/(\d+[A-Za-z]*)/);
                if (num) sectorNumber = num[1];
            }
        }
    }

    if (sectorNumber) {
        let finalString = `SETOR ${sectorNumber}`;
        if (macroRegion) {
            finalString += ` (${macroRegion})`;
        }
        return finalString;
    }

    // Fallback: return original if no number found
    return name;
};

// --- OBSERVAÇÕES: TEXTOS PRÉ-DEFINIDOS ---
// Cada item vira um botão discreto (somente ícone) ao lado do rótulo "Observações".
const NOTE_PRESETS = [
    { id: 'croqui', text: 'SEM INFORMAÇÕES (CROQUI)', icon: PencilRuler },
    { id: 'estruturas', text: 'TROCA DE ESTRUTURAS ELÉTRICAS', icon: Zap },
    { id: 'drops', text: 'VERIFICAR DROPS', icon: Cable },
    { id: 'poda', text: 'PODA DE ÁRVORES', icon: Trees },
];

const NEIGHBORHOOD_PRESETS = [
    { id: 'not-informed', text: 'Não informado', icon: MapPinOff },
    { id: 'rural', text: 'Rural', icon: Wheat },
];

const NOTE_SEPARATOR = ' / ';

// Verifica se o texto pré-definido já é um dos trechos separados por barra.
const hasNotePreset = (notes, presetText) =>
    (notes || '').split('/').some(part => part.trim().toUpperCase() === presetText);

// Concatena o texto pré-definido, usando " / " quando já existe conteúdo (ignorando espaços em branco).
const appendNotePreset = (notes, presetText) => {
    const current = (notes || '').trim();
    if (current === '') return presetText;
    if (hasNotePreset(current, presetText)) return current;
    return `${current}${NOTE_SEPARATOR}${presetText}`;
};

const ImageWithLoader = ({ primarySrc, fallbackSrc, alt, className, imgClassName = 'object-cover' }) => {
    const [loading, setLoading] = useState(true);
    const [currentSrc, setCurrentSrc] = useState(primarySrc || fallbackSrc);

    const handleError = () => {
        if (currentSrc === primarySrc && fallbackSrc) {
            setCurrentSrc(fallbackSrc);
        } else {
            setLoading(false);
        }
    };

    return (
        <div className={`relative bg-slate-200 dark:bg-slate-700 ${className}`}>
            {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <Loader className="animate-spin h-8 w-8 text-slate-500" />
                </div>
            )}
            <img
                src={currentSrc}
                alt={alt}
                className={`w-full h-full ${imgClassName} transition-opacity duration-300 ${loading ? 'opacity-0' : 'opacity-100'}`}
                onLoad={() => setLoading(false)}
                onError={handleError}
            />
        </div>
    );
};

const resizeImage = (file, maxWidth, maxHeight) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      let { width, height } = img;

      if (width > height) {
        if (width > maxWidth) {
          height = Math.round(height * (maxWidth / width));
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round(width * (maxHeight / height));
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(img.src);
          if (blob) {
            const newFileName = file.name.replace(/(\.[\w\d_-]+)$/i, '_thumb.jpeg');
            resolve(new File([blob], newFileName, { type: 'image/jpeg', lastModified: Date.now() }));
          } else {
            reject(new Error('Falha ao criar o blob da miniatura.'));
          }
        },
        'image/jpeg',
        0.8
      );
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(img.src);
      reject(err);
    };
  });
};


const TradeModal = ({
    isOpen,
    onClose,
    onSave,
    onDelete,
    onDeleteFile,
    onFileDownload,
    trade,
    cities,
    isProcessing,
    setModalMessage,
    userRole,
    theme,
    darkMapEnabled,
    currentUser,
    onSavePhoto,
    onDeletePhoto,
    usersMap,
    existingTrades
}) => {
    const [formData, setFormData] = useState({});
    const [scrapeUrl, setScrapeUrl] = useState('');
    const [isScraping, setIsScraping] = useState(false);
    const [isGeocoding, setIsGeocoding] = useState(false);
    const [filesToUpload, setFilesToUpload] = useState([]);
    const [isCopied, setIsCopied] = useState(false);
    const [downloading, setDownloading] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [manualCoords, setManualCoords] = useState('');
    const [isTakingPhoto, setIsTakingPhoto] = useState(false);
    const [photoProgressMsg, setPhotoProgressMsg] = useState('');
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
	const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
    const [pdfFile, setPdfFile] = useState(null);
    const [expandedSections, setExpandedSections] = useState({ data: true, map: false });
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [zoomedPhoto, setZoomedPhoto] = useState(null);
    const [isDuplicate, setIsDuplicate] = useState(false);

    // Verifica se já existe uma troca de postes com o mesmo número (serviceNote) no sistema
    const checkDuplicate = useCallback((noteNumber) => {
        if (!noteNumber || !existingTrades) return false;
        const cleanNumber = noteNumber.trim().toLowerCase();

        return existingTrades.some(t => {
            const isSameNote = t.serviceNote && t.serviceNote.trim().toLowerCase() === cleanNumber;
            const isDifferentId = trade ? t.id !== trade.id : true;
            return isSameNote && isDifferentId;
        });
    }, [existingTrades, trade]);

    const handleServiceNoteBlur = (e) => {
        const value = e.target.value;
        if (value && value.trim() !== '') {
            const isDup = checkDuplicate(value);
            setIsDuplicate(isDup);
            if (isDup) {
                setModalMessage(`Já existe uma troca de postes cadastrada com o número de nota "${value}". O cadastro não poderá prosseguir.`);
            }
        } else {
            setIsDuplicate(false);
        }
    };

    const handleClose = () => {
        if (isDuplicate && formData.serviceNote) {
            onClose(formData.serviceNote);
        } else {
            onClose();
        }
    };

    const viewedTradeRef = useRef(null);
    const menuRef = useRef(null);
    const photoInputRef = useRef(null);

    const { ozmapData, isLoading: ozmapLoading, error: ozmapError } = useOzmapData({
        projectName: formData.city ? formData.city.toUpperCase() : '',
        centerCoordinates: formData.coordinates,
        radius: 500
    });

    const handleRegionFound = useCallback((regionName) => {
        const formattedName = formatSectorName(regionName);
        setFormData(prev => {
            if (prev.ftthSector !== formattedName) {
                return { ...prev, ftthSector: formattedName };
            }
            return prev;
        });
    }, []);

    // Fechar menu ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Efeito para identificar a região e preencher o formulário
    useEffect(() => {
        if (!ozmapData?.regioes || !formData.coordinates) return;

        // Se estiver marcado como "Sem Rede", não buscamos o setor
        if (formData.isNoNetwork) return;

        let shouldUpdate = true;

        if (trade) {
            const dbLat = trade.coordinates?.lat;
            const dbLng = trade.coordinates?.lng;
            const currentLat = formData.coordinates.lat;
            const currentLng = formData.coordinates.lng;

            const coordsChanged = (dbLat !== currentLat || dbLng !== currentLng);

            const dbSectorEmpty = !trade.ftthSector || trade.ftthSector === 'N/A' || trade.ftthSector.trim() === '';
            const dbIsNoNetwork = trade.isNoNetwork;

            if (!coordsChanged) {
                 // Se coordenadas não mudaram, só atualiza se estiver vazio e não for intencional (NoNetwork)
                 if (dbSectorEmpty && !dbIsNoNetwork) {
                     shouldUpdate = true;
                 } else {
                     shouldUpdate = false;
                 }
            }
        }

        if (shouldUpdate) {
            const foundRegion = ozmapData.regioes.find(regiao =>
                regiao.coordinates && isPointInPolygon(formData.coordinates, regiao.coordinates)
            );
            if (foundRegion && foundRegion.name) {
                handleRegionFound(foundRegion.name.toUpperCase());
            }
        }
    }, [ozmapData, formData.coordinates, formData.isNoNetwork, handleRegionFound, trade]);

    const canEditTrade = userRole === 'administrador' || userRole === 'tecnico';
    const canManagePhotos = userRole === 'administrador' || userRole === 'tecnico' || userRole === 'tecnico_campo';
    const isReadOnly = trade ? !canEditTrade : false;
    const isMapDark = theme === 'dark' && darkMapEnabled;

    const toInputDate = (dateString) => {
        if (!dateString || typeof dateString !== 'string') return '';
        const parts = dateString.split('/');
        if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
        return dateString;
    };
    const isValidTime = (timeString) => {
        if (!timeString) return true;
        return /^([01]\d|2[0-3]):([0-5]\d)$/.test(timeString);
    };

    const initializeForm = useCallback((tradeData = null) => {
        const initialData = tradeData || {};
        const coords = initialData.coordinates;
        setFormData({
            serviceNote: initialData.serviceNote || '',
            technicianName: initialData.technicianName || '',
            technicianPhone: initialData.technicianPhone || '',
            servicePeriodStart: toInputDate(initialData.servicePeriodStart),
            servicePeriodEnd: toInputDate(initialData.servicePeriodEnd),
            startTime: initialData.startTime || '',
            endTime: initialData.endTime || '',
            address: initialData.address || '',
            neighborhood: initialData.neighborhood || '',
            coordinates: coords || null,
            description: initialData.description || '',
            technicianNotes: initialData.technicianNotes || '',
            city: initialData.city || (cities.length > 0 ? cities[0].nome : ''),
            ftthSector: initialData.ftthSector || '',
            poles: initialData.poles || 0,
            isCritical: initialData.isCritical || false,
            isNoNetwork: initialData.isNoNetwork || false,
            isCanceled: initialData.isCanceled || false,
            files: initialData.files || [],
            photos: initialData.photos || [],
        });
        setManualCoords(coords ? `${coords.lat}, ${coords.lng}` : '');
        setFilesToUpload([]);
    }, [cities]);

    useEffect(() => {
        if (isOpen) {
            // Only log the 'view' activity if the trade ID has changed since the last view.
            if (trade && trade.id !== viewedTradeRef.current) {
                logActivity('view', 'trade', trade.id, null, trade.serviceNote);
                viewedTradeRef.current = trade.id;
            }
            setExpandedSections({ data: true, map: false });
            setShowDeleteConfirm(false);
            initializeForm(trade);
            setScrapeUrl('');
        } else {
            // Reset when the modal is closed to allow logging again if reopened
            viewedTradeRef.current = null;
        }
    }, [trade, isOpen, initializeForm]);

    useEffect(() => {
        if (trade) { setFormData(prev => ({...prev, photos: trade.photos || []})); }
    }, [trade]);

    useEffect(() => {
        setManualCoords(formData.coordinates ? `${formData.coordinates.lat}, ${formData.coordinates.lng}` : '');
    }, [formData.coordinates]);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const handleReverseGeocode = async ({ lat, lng }) => {
        setIsGeocoding(true);
        try {
            const result = await reverseGeocode(lat, lng);
            if (result.success) {
                const { street, neighborhood, city } = result.data;
                setFormData(prev => ({
                    ...prev,
                    address: street || prev.address,
                    neighborhood: prev.neighborhood && prev.neighborhood.trim() !== '' ? prev.neighborhood : neighborhood,
                    city: city || prev.city,
                }));
            }
        } catch (error) {
            console.error("Erro na geolocalização reversa:", error);
            setModalMessage("Não foi possível buscar o endereço para as coordenadas.");
        } finally {
            setIsGeocoding(false);
        }
    };

    const handleFetchData = async () => {
        if (!scrapeUrl.trim()) { setModalMessage('Por favor, insira uma URL válida.'); return; }
        setIsScraping(true);
        try {
            const result = await scrapeCpflUrl(scrapeUrl);
            if (result.success) {
                const scrapedData = result.data;
                const importedNote = scrapedData.serviceNote;

                // Verifica duplicidade do número da nota logo após a obtenção dos dados
                let isDup = false;
                if (importedNote) {
                    isDup = checkDuplicate(importedNote);
                    setIsDuplicate(isDup);
                }

                setFormData(prev => ({
                    ...prev,
                    serviceNote: importedNote || prev.serviceNote,
                    technicianName: scrapedData.technicianName || prev.technicianName,
                    technicianPhone: scrapedData.technicianPhone || prev.technicianPhone,
                    servicePeriodStart: toInputDate(scrapedData.servicePeriodStart) || prev.servicePeriodStart,
                    servicePeriodEnd: toInputDate(scrapedData.servicePeriodEnd) || prev.servicePeriodEnd,
                    startTime: scrapedData.startTime || prev.startTime,
                    endTime: scrapedData.endTime || prev.endTime,
                    address: scrapedData.fullAddress || prev.address,
                    city: scrapedData.city || prev.city,
                    coordinates: scrapedData.coordinates || prev.coordinates,
                    description: scrapedData.description ? `${scrapedData.description}\n${prev.description}`.trim() : prev.description,
                }));
                if (scrapedData.coordinates) { handleReverseGeocode(scrapedData.coordinates); }

                if (isDup) {
                    setModalMessage(`Dados extraídos com sucesso, mas a nota de serviço "${importedNote}" já está cadastrada no sistema. O cadastro não poderá prosseguir.`);
                } else {
                    setModalMessage('Dados extraídos com sucesso!');
                }
                setScrapeUrl('');
            } else { throw new Error(result.message || 'A função retornou um erro inesperado.'); }
        } catch (error) {
            console.error("Erro ao chamar a Cloud Function:", error);
            setModalMessage(`Não foi possível buscar os dados. Detalhes: ${error.message}`);
        } finally {
            setIsScraping(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'serviceNote') {
            setIsDuplicate(false);
        }
        if (name === 'isNoNetwork') {
            setFormData(prev => ({ ...prev, isNoNetwork: checked, ftthSector: checked ? 'N/A' : '' }));
        } else {
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    const handleAddNotePreset = (presetText) => {
        if (isReadOnly) return;
        setFormData(prev => ({ ...prev, description: appendNotePreset(prev.description, presetText) }));
    };

    const handleSetNeighborhoodPreset = (presetText) => {
        if (isReadOnly) return;
        setFormData(prev => ({ ...prev, neighborhood: presetText }));
    };

    const handleSetRuralAddress = () => {
        if (isReadOnly) return;
        setFormData(prev => ({ ...prev, address: 'Rural' }));
    };

    const handleFilesChange = (event) => {
        setFilesToUpload(Array.from(event.target.files));
    };

    const handleRemoveFile = (indexToRemove) => {
        setFilesToUpload(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
    };

    const handleDeleteRequest = (type, data) => {
        setItemToDelete({ type, data });
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = () => {
        if (!itemToDelete) return;
        if (itemToDelete.type === 'trade') { onDelete(itemToDelete.data.id); }
        else if (itemToDelete.type === 'file') { onDeleteFile(itemToDelete.data.tradeId, itemToDelete.data.file); }
        else if (itemToDelete.type === 'photo') { onDeletePhoto(itemToDelete.data.tradeId, itemToDelete.data.photo); }
        setShowDeleteConfirm(false);
        setItemToDelete(null);
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setItemToDelete(null);
    };

    const handleCopyToClipboard = () => {
        if (manualCoords) {
            const textArea = document.createElement("textarea");
            textArea.value = manualCoords;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        }
    };

    const parseManualCoords = (coordsString) => {
        if (!coordsString || coordsString.trim() === '') return null;

        // Tenta converter DMS primeiro
        const dmsCoords = parseDMSCoordinates(coordsString);
        if (dmsCoords) {
            return dmsCoords;
        }

        // Tenta converter decimal com ou sem vírgula, ponto e vírgula, etc
        const decimalCoords = parseDecimalCoordinates(coordsString);
        if (decimalCoords) {
            return decimalCoords;
        }

        // Tenta fallback manual
        const parts = coordsString.split(',').map(s => s.trim());
        if (parts.length === 2) {
            const lat = parseFloat(parts[0]);
            const lng = parseFloat(parts[1]);
            if (!isNaN(lat) && lat >= -90 && lat <= 90 && !isNaN(lng) && lng >= -180 && lng <= 180) {
                return { lat, lng };
            }
        }

        return null;
    };

    const handleCoordinatesBlur = () => {
        if (isReadOnly) return;

        if (!manualCoords || manualCoords.trim() === '') {
            setFormData(prev => ({ ...prev, coordinates: null }));
            return;
        }

        const parsedCoords = parseManualCoords(manualCoords);

        if (parsedCoords) {
            const { lat, lng } = parsedCoords;
            const newCoords = { lat, lng };
            setManualCoords(`${lat.toFixed(6)}, ${lng.toFixed(6)}`);
            setFormData(prev => ({ ...prev, coordinates: newCoords }));
            handleReverseGeocode(newCoords);
        } else {
            setFormData(prev => ({ ...prev, coordinates: null }));
            setModalMessage('Coordenadas inválidas. Verifique os valores ou o formato inserido.');
        }
    };

    const handleUpdateCoordinates = () => {
        handleCoordinatesBlur();
    };

    const handleDownloadClick = async (file, downloadKey) => {
        if (!file.path || downloading) return;

        const isPdf = file.name && file.name.toLowerCase().endsWith('.pdf');

        if (isPdf) {
            setPdfFile(file);
            setIsPdfViewerOpen(true);
        } else {
            setDownloading(downloadKey);
            let downloadName = file.name;

            if (downloadKey.startsWith('photo-')) {
                const timestamp = file.createdAt?.toDate ? file.createdAt.toDate().getTime() : Date.now();
                const serviceNote = formData.serviceNote || 'UNKNOWN';
                const originalName = file.name || 'photo.jpg';
                const extension = originalName.split('.').pop();
                downloadName = `PHOTO_NS_${serviceNote}_${timestamp}.${extension}`;
            }

            await onFileDownload(file.path, downloadName);
            setDownloading(null);
        }
    };

    // =================================================================================
    // INÍCIO DA FUNÇÃO DE MARCA D'ÁGUA - CORREÇÕES APLICADAS AQUI
    // =================================================================================
    const processAndWatermarkImage = (file, currentUser, usersMap, tradeAddress) => {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                return reject(new Error("Geolocalização não é suportada por este navegador."));
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const coordsText = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;

                    const objectUrl = URL.createObjectURL(file);
                    const img = new Image();
                    img.onload = () => {
                            URL.revokeObjectURL(objectUrl);
                            const canvas = document.createElement('canvas');
                            const ctx = canvas.getContext('2d');

                            // Redimensiona a imagem para uma largura máxima de 1920px
                            const MAX_WIDTH = 1920;
                            let width = img.width;
                            let height = img.height;

                            if (width > MAX_WIDTH) {
                                height = (height * MAX_WIDTH) / width;
                                width = MAX_WIDTH;
                            }
                            canvas.width = width;
                            canvas.height = height;
                            ctx.drawImage(img, 0, 0, width, height);

                            // Prepara os textos para a marca d'água
                            const photoTakenBy = usersMap[currentUser?.uid]?.displayName || currentUser?.displayName || 'Usuário Desconhecido';
                            const dateObj = new Date();
                            const yearToDisplay = dateObj.getFullYear();
                            const dayMonthToDisplay = dateObj.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long' });
                            const timeToDisplay = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                            const addressLine = tradeAddress || 'Endereço não disponível';

                            // Ícone de globo para as coordenadas
                            const globeSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>`;
                            const globeIconImage = new Image();
                            globeIconImage.src = 'data:image/svg+xml;base64,' + btoa(globeSvg);

                            const logo = new Image();
                            logo.src = logoConnect10;

                            // Função para desenhar a marca d'água
                            const drawWatermark = () => {
                                // --- CONFIGURAÇÕES GERAIS ---
                                const margin = Math.max(20, Math.round(width / 60));
                                ctx.fillStyle = 'white';
                                ctx.textAlign = 'left';
                                ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
                                ctx.shadowOffsetX = 2;
                                ctx.shadowOffsetY = 2;
                                ctx.shadowBlur = 4;

                                // --- DEFINIÇÃO DE FONTES RESPONSIVAS ---
                                const timeFontSize = Math.max(32, Math.round(width / 25));
                                const dateFontSize = Math.max(16, Math.round(width / 60));
                                const infoFontSize = Math.max(14, Math.round(width / 75));

                                // --- ALTURAS DE LINHA E ESPAÇAMENTOS ---
                                const dateLineHeight = dateFontSize * 1.1;
                                const infoLineHeight = infoFontSize * 1.4;
                                const blockSpacing = margin * 0.5; // Espaçamento reduzido entre os blocos

                                // --- CÁLCULO PRELIMINAR DA LARGURA MÁXIMA ---
                                ctx.font = `bold ${timeFontSize}px Arial`;
                                const timeMetrics = ctx.measureText(timeToDisplay);
                                const separatorXCalc = margin + timeMetrics.width + (margin * 0.5);
                                const dateBlockXCalc = separatorXCalc + (margin * 0.5) + 3;
                                ctx.font = `${dateFontSize}px Arial`;
                                const dateTextWidthCalc = Math.max(ctx.measureText(yearToDisplay).width, ctx.measureText(dayMonthToDisplay).width);
                                const dynamicWidth = (dateBlockXCalc + dateTextWidthCalc) - margin;

                                // --- POSICIONAMENTO (DE BAIXO PARA CIMA) ---
                                let currentY = canvas.height - margin;

                                // 1. ENDEREÇO
                                ctx.font = `${infoFontSize}px Arial`;
                                ctx.textBaseline = 'bottom';
                                ctx.fillText(addressLine, margin, currentY);
                                currentY -= infoLineHeight;

                                // 2. COORDENADAS (com ícone)
                                const globeIconSize = infoFontSize * 1.1;
                                const globeIconPadding = infoFontSize * 0.5;
                                const textCoordsX = margin + globeIconSize + globeIconPadding;
                                const globeIconY = currentY - globeIconSize - (infoFontSize * 0.1);
                                if (globeIconImage.complete && globeIconImage.naturalWidth !== 0) {
                                    ctx.drawImage(globeIconImage, margin, globeIconY, globeIconSize, globeIconSize);
                                }
                                ctx.fillText(coordsText, textCoordsX, currentY);
                                currentY -= infoLineHeight;

                                // 3. NOME DO TÉCNICO
                                ctx.fillText(photoTakenBy, margin, currentY);
                                currentY -= infoLineHeight;

                                // 3.5 LINHA SEPARADORA E ESPAÇAMENTO
                                currentY -= blockSpacing; // Adiciona espaço entre técnico e a linha
                                ctx.save();
                                ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
                                ctx.shadowColor = 'transparent';
                                ctx.fillRect(margin, currentY, dynamicWidth, 1); // Desenha a linha
                                ctx.restore();
                                currentY -= blockSpacing; // Adiciona espaço entre a linha e o bloco de tempo

                                // --- BLOCO DE HORA E DATA (ALINHAMENTO COM 'textBaseline: middle') ---
                                const timeBlockHeight = timeFontSize;
                                const timeBlockCenterY = currentY - (timeBlockHeight / 2);

                                // 4. HORA (GRANDE)
                                ctx.font = `bold ${timeFontSize}px Arial`;
                                ctx.textBaseline = 'middle';
                                ctx.fillText(timeToDisplay, margin, timeBlockCenterY);

                                // 5. BARRA SEPARADORA
                                const separatorX = margin + timeMetrics.width + (margin * 0.5);
                                const separatorHeight = timeFontSize * 0.8;
                                ctx.fillStyle = '#FFA500';
                                ctx.fillRect(separatorX, timeBlockCenterY - (separatorHeight / 2), 3, separatorHeight);
                                ctx.fillStyle = 'white';

                                // 6. DATA (DUAS LINHAS)
                                ctx.font = `${dateFontSize}px Arial`;
                                const dateBlockX = separatorX + (margin * 0.5) + 3;
                                ctx.fillText(yearToDisplay, dateBlockX, timeBlockCenterY - (dateLineHeight / 2));
                                ctx.fillText(dayMonthToDisplay, dateBlockX, timeBlockCenterY + (dateLineHeight / 2));
                                
                                // Atualiza a posição Y para acima do bloco de hora/data
                                currentY -= timeBlockHeight;

                                // 7. LOGOTIPO
                                if (logo.complete && logo.naturalWidth !== 0) {
                                    currentY -= blockSpacing; // Espaço antes do logo
                                    let logoHeight = Math.max(40, Math.round(height / 18));
                                    let logoWidth = (logo.width / logo.height) * logoHeight;
                                
                                    if (logoWidth > dynamicWidth) {
                                        logoHeight = (logoHeight * dynamicWidth) / logoWidth;
                                        logoWidth = dynamicWidth;
                                    }
                                
                                    const logoY = currentY - logoHeight;
                                    ctx.drawImage(logo, margin, logoY, logoWidth, logoHeight);
                                }

                                // Converte o canvas para blob e resolve a promise
                                canvas.toBlob(
                                    (blob) => {
                                        canvas.width = 0;
                                        canvas.height = 0;
                                        if (blob) {
                                            const newFileName = file.name.replace(/\.\w+$/, '.jpg');
                                            resolve(new File([blob], newFileName, { type: 'image/jpeg', lastModified: Date.now() }));
                                        } else {
                                            reject(new Error("Falha ao criar blob da imagem com marca d'água."));
                                        }
                                    },
                                    'image/jpeg',
                                    0.85
                                );
                            };

                            // Garante que as imagens (logo, ícone) sejam carregadas antes de desenhar
                            let imagesLoaded = 0;
                            const totalImages = 2;
                            const onImageLoad = () => {
                                imagesLoaded++;
                                if (imagesLoaded === totalImages) {
                                    drawWatermark();
                                }
                            };
                            logo.onload = onImageLoad;
                            globeIconImage.onload = onImageLoad;
                            logo.onerror = () => { console.warn("Logo não pôde ser carregado."); onImageLoad(); };
                            globeIconImage.onerror = () => { console.warn("Ícone de globo não pôde ser carregado."); onImageLoad(); };

                            // Se as imagens já estiverem em cache, chama a função diretamente
                            if (logo.complete) onImageLoad();
                            if (globeIconImage.complete) onImageLoad();
                        };
                    img.onerror = () => {
                        URL.revokeObjectURL(objectUrl);
                        reject(new Error("Não foi possível carregar a imagem para processamento."));
                    };
                    img.src = objectUrl;
                },
                (error) => { reject(error); },
                { enableHighAccuracy: true, timeout: 15000, maximumAge: 120000 }
            );
        });
    };
    // =================================================================================
    // FIM DA FUNÇÃO DE MARCA D'ÁGUA
    // =================================================================================


    const handlePhotoCapture = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsTakingPhoto(true);
        setPhotoProgressMsg('Preparando imagem...');
        try {
            const deviceRam = (typeof navigator !== 'undefined' && navigator.deviceMemory) || 4;
            const maxWidthOrHeight = deviceRam <= 2 ? 1280 : 1920;

            setPhotoProgressMsg('Comprimindo imagem...');
            const preparedFile = await imageCompression(file, {
                maxWidthOrHeight,
                useWebWorker: true,
                fileType: 'image/jpeg',
                initialQuality: 0.92,
            });

            setPhotoProgressMsg('Obtendo localização GPS...');
            const tradeAddress = formData.address;

            setPhotoProgressMsg('Aplicando marca d\'água...');
            const processedPhoto = await processAndWatermarkImage(preparedFile, currentUser, usersMap, tradeAddress);

            setPhotoProgressMsg('Gerando miniatura...');
            const thumbnailPhoto = await resizeImage(processedPhoto, 400, 400);

            setPhotoProgressMsg('Enviando foto...');
            await onSavePhoto(trade.id, processedPhoto, thumbnailPhoto);

            setModalMessage("Foto e miniatura adicionadas com sucesso!");
        } catch (error) {
            console.error("Erro ao capturar ou processar foto:", error);
            let message = "Não foi possível adicionar a foto. " + (error.message || '');
            if (error.code) {
                switch (error.code) {
                    case 1: message = "Permissão de localização negada. A geolocalização é necessária para a marca d'água."; break;
                    case 2: message = "Não foi possível obter a localização atual. Verifique o sinal de GPS ou a conexão."; break;
                    case 3: message = "Tempo esgotado ao obter a localização. Tente novamente."; break;
                    default: message = `Erro de localização desconhecido (código: ${error.code}).`;
                }
            }
            setModalMessage(message);
        } finally {
            setIsTakingPhoto(false);
            setPhotoProgressMsg('');
            if (photoInputRef.current) {
                photoInputRef.current.value = "";
            }
        }
    };

    const handleGeneratePdf = async () => {
        if (!formData.photos || formData.photos.length === 0) {
            setModalMessage("Nenhuma foto para gerar o relatório.");
            return;
        }
        setIsGeneratingPdf(true);
        try {
            await generateTradeReportPDF(formData, formData.photos, usersMap);
        } catch (error) {
            setModalMessage(error.message);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!canManagePhotos) return;

        // Impede o salvamento se o número da troca for duplicado no sistema
        if (checkDuplicate(formData.serviceNote)) {
            setModalMessage(`Não é possível salvar. Já existe uma troca de postes cadastrada com o número de nota "${formData.serviceNote}".`);
            return;
        }

        if (userRole === 'administrador' || userRole === 'tecnico') {
            const requiredFields = [
                { key: 'serviceNote', label: 'Número da Nota de Serviço (TES)' },
                { key: 'servicePeriodStart', label: 'Data Início' },
                { key: 'servicePeriodEnd', label: 'Data Fim' },
                { key: 'startTime', label: 'Hora Início' },
                { key: 'endTime', label: 'Hora Fim' },
                { key: 'address', label: 'Endereço / Ponto de Referência' },
                { key: 'neighborhood', label: 'Bairro' },
                { key: 'city', label: 'Cidade' },
                { key: 'ftthSector', label: 'Área afetada' }
            ];
            for (const field of requiredFields) {
                if (!formData[field.key]) {
                    setModalMessage(`O campo "${field.label}" é obrigatório.`);
                    return;
                }
            }
            if (!isValidTime(formData.startTime) || !isValidTime(formData.endTime)) {
                setModalMessage('Por favor, insira um horário válido no formato HH:MM (ex: 14:30).');
                return;
            }

            let coordsToSave = formData.coordinates;
            if (manualCoords && manualCoords.trim() !== '') {
                const parsed = parseManualCoords(manualCoords);
                if (!parsed) {
                    setModalMessage('As coordenadas inseridas são inválidas. Corrija-as antes de salvar.');
                    return;
                }
                coordsToSave = parsed;
            } else {
                coordsToSave = null;
            }

            const { status, ...restOfData } = formData;
            const dataToSave = {
                ...restOfData,
                servicePeriodStart: formData.servicePeriodStart,
                servicePeriodEnd: formData.servicePeriodEnd,
                coordinates: coordsToSave
            };

            onSave(dataToSave, filesToUpload, trade ? trade.id : null, false);
        }
        else if (userRole === 'tecnico_campo') {
            if (trade && trade.id) {
                const partialData = { technicianNotes: formData.technicianNotes };
                onSave(partialData, [], trade.id, true);
            } else {
                setModalMessage("Você não tem permissão para criar uma nova troca.");
            }
        }
    };

    if (!isOpen) return null;

    // 'ready' com 0 é um resultado válido; 'error' significa que a API do Ozmap
    // não respondeu e exibir "0" seria uma informação errada.
    const clientsInRadiusLabel = !formData.coordinates
        ? 'informe as coordenadas'
        : ozmapData?.clientsStatus === 'ready'
            ? ozmapData.clientsCount.toLocaleString('pt-BR')
            : ozmapData?.clientsStatus === 'error'
                ? 'não disponível'
                : 'calculando...';

    const labelStyle = `block text-sm font-semibold mb-2 ${isReadOnly ? 'text-slate-500' : 'text-slate-600 dark:text-slate-300'}`;
    const inputStyle = `input-style w-full px-3 py-2 ${isReadOnly ? 'bg-slate-100 dark:bg-slate-700/50 cursor-not-allowed' : ''}`;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[1050] flex justify-center items-center p-0 md:p-4 animate-fade-in">

            <PDFViewerModal
                isOpen={isPdfViewerOpen}
                onClose={() => setIsPdfViewerOpen(false)}
                onDownload={() => onFileDownload(pdfFile.path, pdfFile.name)}
                fileUrl={pdfFile?.url}
                fileName={pdfFile?.name}
            />

            <ConfirmDialog
                isOpen={showDeleteConfirm && itemToDelete?.type !== 'trade'}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={`Excluir ${itemToDelete?.type === 'photo' ? 'Foto' : 'Arquivo'}`}
                message={`Você tem certeza que deseja excluir permanentemente est${itemToDelete?.type === 'photo' ? 'a foto' : 'e arquivo'}? Esta ação não pode ser desfeita.`}
                isProcessing={isProcessing}
            />

            <ConfirmDialog
                isOpen={showDeleteConfirm && itemToDelete?.type === 'trade'}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Excluir Troca"
                message="Você tem certeza que deseja excluir permanentemente esta troca? Todos os arquivos e fotos associados também serão removidos. Esta ação não pode ser desfeita."
                isProcessing={isProcessing}
            />

            <div className="bg-white dark:bg-slate-800 md:rounded-2xl shadow-2xl w-full max-w-7xl flex flex-col h-full md:h-[90vh] overflow-hidden" role="dialog" aria-modal="true">

                {/* --- HEADER --- */}
                <header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 flex-shrink-0 bg-white dark:bg-slate-800 z-30">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleClose}
                            className="md:hidden p-2 -ml-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                            aria-label="Voltar"
                            title="Voltar"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 truncate max-w-[200px] md:max-w-none">
                            {trade ? (isReadOnly ? 'Visualizar Troca' : 'Editar Troca') : 'Nova Troca de Poste'}
                        </h2>
                    </div>
                    <div className="flex items-center gap-2">
                        {canManagePhotos && (
                             <button onClick={handleSubmit} type="submit" className="hidden md:flex btn-primary items-center py-1.5 px-3 text-sm" disabled={isProcessing} title="Salvar">
                                {isProcessing ? <Loader className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                                <span className="hidden sm:inline">{isProcessing ? 'Salvando...' : 'Salvar'}</span>
                            </button>
                        )}

                        {/* CONTEXT MENU */}
                        {trade && canEditTrade && (
                            <div className="relative" ref={menuRef}>
                                <button
                                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                                    className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                                    aria-label="Mais opções"
                                    aria-expanded={isMenuOpen}
                                >
                                    <MoreVertical size={20} />
                                </button>
                                {isMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-md shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-50 animate-in fade-in zoom-in-95 duration-100">
                                        <button
                                            onClick={() => {
                                                setIsMenuOpen(false);
                                                handleDeleteRequest('trade', { id: trade.id });
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center"
                                        >
                                            <Trash2 size={16} className="mr-2" /> Excluir
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                        <button
                            onClick={handleClose}
                            className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors hidden md:block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                            aria-label="Fechar"
                            title="Fechar"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </header>

                {/* --- MAIN CONTENT (SPLIT VIEW) --- */}
                <div className="flex-1 overflow-y-auto md:overflow-hidden flex flex-col md:grid md:grid-cols-2 relative">

                    {/* --- COLUMN 1: DATA FORM (LEFT) --- */}
                    <div className="p-4 md:p-6 md:border-r md:border-slate-200 dark:md:border-slate-700 space-y-6 md:h-full md:overflow-y-auto pb-4 md:pb-6">

                         {/* Mobile Accordion Header for Data */}
                         <div
                            className="md:hidden flex justify-between items-center bg-slate-100 dark:bg-slate-700/50 p-3 rounded-lg cursor-pointer"
                            onClick={() => toggleSection('data')}
                            role="button"
                            tabIndex={0}
                            aria-expanded={expandedSections.data}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleSection('data')}
                        >
                            <h3 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2"><Info size={18} /> Dados da Troca</h3>
                            {expandedSections.data ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>

                         {/* Form Content (Collapsible on Mobile) */}
                         <div className={`${expandedSections.data ? '' : 'hidden'} md:block space-y-6`}>
                            {!trade && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                    <label htmlFor="scrapeUrl" className="block text-sm font-bold text-blue-800 dark:text-blue-200 mb-2">Importar Dados da URL da CPFL</label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input type="url" id="scrapeUrl" value={scrapeUrl} onChange={(e) => setScrapeUrl(e.target.value)} placeholder="Cole a URL aqui" className={`${inputStyle} flex-grow`} />
                                        <button type="button" onClick={handleFetchData} disabled={isScraping} className="btn-secondary w-full sm:w-40 shrink-0 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                            {isScraping ? <Loader className="animate-spin" /> : <span className="flex items-center"><DownloadCloud size={18} className="mr-2" /><span>Buscar Dados</span></span>}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                    <div className="md:col-span-2"><label htmlFor="serviceNote" className={labelStyle}>Número da Nota de Serviço (TES)</label><input type="text" id="serviceNote" name="serviceNote" value={formData.serviceNote || ''} onChange={handleChange} onBlur={handleServiceNoteBlur} className={inputStyle} disabled={isReadOnly} required/></div>
                                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-6">
                                        <div><label htmlFor="technicianName" className={labelStyle}>Nome do Técnico (Opcional)</label><input type="text" id="technicianName" name="technicianName" value={formData.technicianName || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly}/></div>
                                        <div><label htmlFor="technicianPhone" className={labelStyle}>Telefone (Opcional)</label><input type="text" id="technicianPhone" name="technicianPhone" value={formData.technicianPhone || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly}/></div>
                                    </div>
                                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-6">
                                        <div><label htmlFor="servicePeriodStart" className={labelStyle}>Data Início</label><input type="date" id="servicePeriodStart" name="servicePeriodStart" value={formData.servicePeriodStart || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly} required/></div>
                                        <div><label htmlFor="servicePeriodEnd" className={labelStyle}>Data Fim</label><input type="date" id="servicePeriodEnd" name="servicePeriodEnd" value={formData.servicePeriodEnd || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly} required/></div>
                                    </div>
                                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-6">
                                        <div><label htmlFor="startTime" className={labelStyle}>Hora Início</label><input type="text" id="startTime" name="startTime" value={formData.startTime || ''} onChange={handleChange} className={inputStyle} placeholder="HH:MM" disabled={isReadOnly} required/></div>
                                        <div><label htmlFor="endTime" className={labelStyle}>Hora Fim</label><input type="text" id="endTime" name="endTime" value={formData.endTime || ''} onChange={handleChange} className={inputStyle} placeholder="HH:MM" disabled={isReadOnly} required/></div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <div className="flex items-center justify-between gap-2 mb-2">
                                            <label htmlFor="address" className={labelStyle}>Endereço / Ponto de Referência</label>
                                            <button
                                                type="button"
                                                onClick={handleSetRuralAddress}
                                                disabled={isReadOnly}
                                                title="Rural"
                                                aria-label="Definir endereço como Rural"
                                                aria-pressed={formData.address === 'Rural'}
                                                className={`p-1.5 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${formData.address === 'Rural'
                                                    ? 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/30'
                                                    : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-700'}`}
                                            >
                                                <Wheat size={16} />
                                            </button>
                                        </div>
                                        <div className="relative">
                                            <input type="text" id="address" name="address" value={formData.address || ''} onChange={handleChange} className={`${inputStyle} pr-10`} disabled={isReadOnly} required/>
                                            {isGeocoding && (
                                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                    <Loader className="animate-spin h-5 w-5 text-blue-500"/>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between gap-2 mb-2">
                                            <label htmlFor="neighborhood" className={labelStyle}>Bairro</label>
                                            <div className="flex items-center gap-0.5">
                                                {NEIGHBORHOOD_PRESETS.map(({ id, text, icon: PresetIcon }) => {
                                                    const isSelected = formData.neighborhood === text;
                                                    return (
                                                        <button
                                                            key={id}
                                                            type="button"
                                                            onClick={() => handleSetNeighborhoodPreset(text)}
                                                            disabled={isReadOnly}
                                                            title={text}
                                                            aria-label={`Definir bairro como "${text}"`}
                                                            aria-pressed={isSelected}
                                                            className={`p-1.5 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${isSelected
                                                                ? 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/30'
                                                                : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-700'}`}
                                                        >
                                                            <PresetIcon size={16} />
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <input type="text" id="neighborhood" name="neighborhood" value={formData.neighborhood || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly} required/>
                                    </div>
                                    <div><label htmlFor="city" className={labelStyle}>Cidade</label><select id="city" name="city" value={formData.city || ''} onChange={handleChange} className={inputStyle} disabled={isReadOnly} required>{cities.map(c => <option key={c.id} value={c.nome}>{c.nome}</option>)}</select></div>
                                    <div className="md:col-span-2"><label htmlFor="coordinates" className={labelStyle}>Coordenadas Geográficas</label><div className="flex items-center gap-2"><input type="text" id="coordinates" value={manualCoords} onChange={(e) => setManualCoords(e.target.value)} onBlur={handleCoordinatesBlur} className={`${inputStyle} h-10`} placeholder="latitude, longitude" disabled={isReadOnly}/><button type="button" onClick={handleUpdateCoordinates} disabled={isReadOnly || isGeocoding} className="btn-secondary h-10 px-3 shrink-0 flex items-center justify-center" title="Buscar endereço para as coordenadas"><Search size={18} /></button><button type="button" onClick={handleCopyToClipboard} className="btn-secondary h-10 px-3 shrink-0 flex items-center justify-center" title="Copiar coordenadas">{isCopied ? <Check size={18} className="text-green-500"/> : <Copy size={18}/>}</button></div></div>
                                    <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-x-6 gap-y-4">
                                        <div>
                                            <label htmlFor="ftthSector" className={labelStyle}>Área afetada</label>
                                            <div className="relative">
                                                <input type="text" id="ftthSector" name="ftthSector" value={formData.ftthSector || ''} onChange={handleChange} className={`${inputStyle} pr-10 ${ozmapLoading ? 'bg-slate-100 text-slate-500' : ''}`} placeholder="Ex.: SETOR 08 / CTO 08.06" disabled={isReadOnly || formData.isNoNetwork || ozmapLoading} required/>
                                                {ozmapLoading && (
                                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                                        <Loader className="animate-spin h-5 w-5 text-blue-500"/>
                                                    </div>
                                                )}
                                            </div>
                                            <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                                                <Users size={13} className="shrink-0" />
                                                <span>Clientes num raio de 500 m: <strong className="font-semibold">{clientsInRadiusLabel}</strong></span>
                                            </p>
                                        </div>
                                        <div className="md:w-32">
                                            <label htmlFor="poles" className={labelStyle}>Postes</label>
                                            <input type="number" id="poles" name="poles" value={formData.poles || 0} onChange={handleChange} min="0" onFocus={(e) => e.target.select()} className={inputStyle} disabled={isReadOnly} />
                                        </div>
                                    </div>

                                    <div className="md:col-span-2">
                                        <div className="flex items-center justify-between gap-2 mb-2">
                                            <label htmlFor="description" className={`text-sm font-semibold ${isReadOnly ? 'text-slate-500' : 'text-slate-600 dark:text-slate-300'}`}>Observações</label>
                                            <div className="flex items-center gap-0.5">
                                                {NOTE_PRESETS.map(({ id, text, icon: PresetIcon }) => {
                                                    const isPresent = hasNotePreset(formData.description, text);
                                                    return (
                                                        <button
                                                            key={id}
                                                            type="button"
                                                            onClick={() => handleAddNotePreset(text)}
                                                            disabled={isReadOnly}
                                                            title={text}
                                                            aria-label={`Adicionar "${text}" às observações`}
                                                            aria-pressed={isPresent}
                                                            className={`p-1.5 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${isPresent
                                                                ? 'text-blue-600 bg-blue-50 dark:text-blue-300 dark:bg-blue-900/30'
                                                                : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-700'}`}
                                                        >
                                                            <PresetIcon size={16} />
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <textarea id="description" name="description" value={formData.description || ''} onChange={handleChange} rows="3" className={inputStyle} disabled={isReadOnly}></textarea>
                                    </div>

                                    <div className="md:col-span-2">
                                        <StyledFileInput
                                            label="Anexar Documentos"
                                            onChange={handleFilesChange}
                                            onRemove={handleRemoveFile}
                                            accept=".doc,.docx,.xls,.xlsx,.pdf,.dwg,.dwf,image/*"
                                            isMultiple={true}
                                            files={filesToUpload}
                                            disabled={isReadOnly}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <h4 className={labelStyle}>Documentos Anexados</h4>
                                        {formData.files && formData.files.length > 0 ? (
                                            <ul className="list-disc list-inside space-y-1 mt-2">
                                                {formData.files.map((file, idx) => (
                                                    <li key={idx} className="text-sm flex items-center justify-between p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800">
                                                        <button type="button" onClick={() => handleDownloadClick(file, `file-${idx}`)} className="text-blue-500 hover:underline disabled:text-slate-400 flex-grow text-left flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label={`Baixar arquivo: ${file.name}`} title={`Baixar ${file.name}`}>
                                                            {downloading === `file-${idx}` ? <Loader className="animate-spin h-4 w-4 inline mr-2"/> : (file.name && file.name.toLowerCase().endsWith('.pdf') ? <Eye size={14} className="inline mr-2"/> : <Download size={14} className="inline mr-2"/>)}
                                                            {file.name}
                                                        </button>
                                                        {canEditTrade && (
                                                            <button type="button" onClick={() => handleDeleteRequest('file', { tradeId: trade.id, file })} className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full transition-colors ml-2" title="Excluir arquivo" aria-label="Excluir arquivo">
                                                                <Trash2 className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="text-sm text-slate-500 dark:text-slate-400">Nenhum documento anexado.</p>
                                        )}
                                    </div>

                                    <div className="md:col-span-2 border-t border-slate-200 dark:border-slate-700 pt-4 mt-2 space-y-3">
                                        <div className="flex items-center">
                                            <input type="checkbox" id="isCritical" name="isCritical" checked={!!formData.isCritical} onChange={handleChange} className="h-5 w-5 rounded text-orange-600 focus:ring-orange-500 border-slate-300 dark:border-slate-600" disabled={isReadOnly}/>
                                            <label htmlFor="isCritical" className="ml-3 flex items-center text-sm font-semibold text-slate-700 dark:text-slate-200"><AlertTriangle size={16} className="mr-1.5 text-orange-500"/> Intervenção Crítica</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input type="checkbox" id="isNoNetwork" name="isNoNetwork" checked={!!formData.isNoNetwork} onChange={handleChange} className="h-5 w-5 rounded text-red-600 focus:ring-red-500 border-slate-300 dark:border-slate-600" disabled={isReadOnly}/>
                                            <label htmlFor="isNoNetwork" className="ml-3 flex items-center text-sm font-semibold text-slate-700 dark:text-slate-200"><WifiOff size={16} className="mr-1.5 text-red-500"/> Sem Rede</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input type="checkbox" id="isCanceled" name="isCanceled" checked={!!formData.isCanceled} onChange={handleChange} className="h-5 w-5 rounded text-slate-600 focus:ring-slate-500 border-slate-300 dark:border-slate-600" disabled={isReadOnly}/>
                                            <label htmlFor="isCanceled" className="ml-3 flex items-center text-sm font-semibold text-slate-700 dark:text-slate-200"><XCircle size={16} className="mr-1.5 text-slate-500"/> Cancelada</label>
                                        </div>
                                    </div>
                                </div>
                            </form>
                         </div>

                        {/* Mobile Accordion Header for Map */}
                        <div
                            className="md:hidden flex justify-between items-center bg-slate-100 dark:bg-slate-700/50 p-3 rounded-lg cursor-pointer mt-4"
                            onClick={() => toggleSection('map')}
                            role="button"
                            tabIndex={0}
                            aria-expanded={expandedSections.map}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleSection('map')}
                        >
                            <h3 className="font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2"><MapIcon size={18} /> Mapa</h3>
                            {expandedSections.map ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </div>

                        <div className={`${expandedSections.map ? '' : 'hidden'} md:block pt-4 md:pt-0`}>
                            <h3 className="hidden md:block text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Localização no Mapa</h3>
                            <OzmapDisplay
                                trade={formData}
                                darkMapEnabled={isMapDark}
                                isVisible={expandedSections.map}
                                ozmapData={ozmapData}
                                isLoading={ozmapLoading}
                                error={ozmapError}
                            />
                        </div>
                    </div>

                    {/* --- COLUMN 2: PHOTOS & ACTIONS (RIGHT) --- */}
                    <div className="flex flex-col md:h-full bg-slate-50 dark:bg-slate-900/30 md:overflow-hidden relative">

                        {/* Photos Header / Dropzone */}
                         {trade?.id ? (
                            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm z-10 sticky top-0 md:relative">
                                {canManagePhotos && (
                                <button
                                    type="button"
                                    onClick={() => photoInputRef.current?.click()}
                                    disabled={isTakingPhoto}
                                    className="w-full flex flex-col items-center justify-center p-6 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-xl bg-blue-50 dark:bg-blue-900/10 hover:bg-blue-100 dark:hover:bg-blue-900/20 transition-all group cursor-pointer"
                                >
                                    {isTakingPhoto ? (
                                        <Loader className="animate-spin text-blue-500 mb-2" size={32} />
                                    ) : (
                                        <Camera className="text-blue-500 mb-2 group-hover:scale-110 transition-transform" size={32} />
                                    )}
                                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                                        {isTakingPhoto ? (photoProgressMsg || 'Processando...') : 'Fotografar'}
                                    </span>
                                    <span className="text-xs text-slate-500 dark:text-slate-400 mt-1 hidden md:block">
                                        As fotos terão marca d'água com nome, data, hora e coordenadas.
                                    </span>
                                </button>
                                )}
                                <input type="file" ref={photoInputRef} className="hidden" accept="image/*" capture="environment" onChange={handlePhotoCapture} />
                            </div>
                        ) : (
                            !trade?.id && (
                                <div className="p-8 text-center text-slate-500">
                                    <Info className="mx-auto mb-2" />
                                    <p>Salve a troca primeiro para habilitar o envio de fotos.</p>
                                </div>
                            )
                        )}

                        {/* Photos Grid - Scrollable Area */}
                        <div className="p-4 pb-24 md:flex-1 md:overflow-y-auto md:pb-40">
                             {formData.photos && formData.photos.length > 0 ? (
                                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {formData.photos.map((photo, index) => {
                                        const now = Date.now();
                                        const photoTimestamp = photo.createdAt?.toDate ? photo.createdAt.toDate().getTime() : 0;
                                        const isWithin72Hours = (now - photoTimestamp) < 72 * 60 * 60 * 1000;
                                        const canDeletePhoto = userRole === 'administrador' || userRole === 'tecnico' || (userRole === 'tecnico_campo' && photo.createdBy === currentUser?.uid && isWithin72Hours);
                                        return (
                                            <div key={index} className="relative group rounded-lg overflow-hidden shadow-md aspect-square bg-slate-200 dark:bg-slate-700">
                                                <ImageWithLoader
                                                    primarySrc={photo.thumbnailURL}
                                                    fallbackSrc={photo.url}
                                                    alt={`Foto da troca ${index + 1}`}
                                                    className="w-full h-full object-cover pointer-events-none"
                                                />
                                                 <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 z-20 pointer-events-none">
                                                    <button onClick={(e) => { e.stopPropagation(); setZoomedPhoto({ photo, index, canDeletePhoto }); }} className="p-2 bg-white/20 hover:bg-white/40 rounded-full text-white backdrop-blur-sm pointer-events-auto focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-900" title="Visualizar foto" aria-label={`Visualizar foto ${index + 1}`}>
                                                        <Eye size={20} />
                                                    </button>
                                                    <button onClick={(e) => { e.stopPropagation(); handleDownloadClick(photo, `photo-dl-${index}`); }} disabled={downloading === `photo-dl-${index}`} className="p-2 bg-white/20 hover:bg-white/40 rounded-full text-white backdrop-blur-sm pointer-events-auto focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-900" title="Baixar foto" aria-label="Baixar foto">
                                                        {downloading === `photo-dl-${index}` ? <Loader className="animate-spin" size={20}/> : <Download size={20} />}
                                                    </button>
                                                    {canDeletePhoto && (
                                                         <button onClick={(e) => { e.stopPropagation(); handleDeleteRequest('photo', { tradeId: trade.id, photo }); }} disabled={isProcessing} className="p-2 bg-red-500/80 hover:bg-red-600 rounded-full text-white backdrop-blur-sm pointer-events-auto focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-offset-slate-900" title="Excluir foto" aria-label="Excluir foto">
                                                            <Trash2 size={20} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                             ) : (
                                trade?.id && (
                                    <div className="h-full flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 opacity-60">
                                        <ImageIcon size={48} className="mb-2" />
                                        <p>Nenhuma foto ainda</p>
                                    </div>
                                )
                             )}
                        </div>

                         {/* --- ACTIONS STICKY FOOTER (RIGHT COL) --- */}
                         {trade?.id && (
                            <div className="sticky bottom-0 md:absolute md:bottom-0 left-0 right-0 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-20">
                                <div className="space-y-4">
                                     <div>
                                        <label htmlFor="technicianNotes" className={labelStyle}>Observações do Técnico</label>
                                        <textarea id="technicianNotes" name="technicianNotes" value={formData.technicianNotes || ''} onChange={handleChange} rows="3" className="input-style w-full px-3 py-2 text-sm" placeholder="Adicione notas ou observações que aparecerão no relatório final..." disabled={!canManagePhotos}></textarea>
                                     </div>

                                     {formData.photos && formData.photos.length > 0 && (
                                        <button onClick={handleGeneratePdf} disabled={isGeneratingPdf || isProcessing} className="w-full btn-secondary flex justify-center items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                                            {isGeneratingPdf ? <Loader className="animate-spin mr-2" /> : <FileText className="mr-2" />}
                                            <span>{isGeneratingPdf ? 'Gerando...' : 'Gerar Relatório PDF'}</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                         )}

                         {/* --- ZOOMED PHOTO OVERLAY --- */}
                         {zoomedPhoto && (
                             <div className="absolute inset-0 bg-white dark:bg-slate-800 z-50 flex flex-col animate-in fade-in zoom-in-95 duration-200">
                                 {/* Header */}
                                 <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 shrink-0">
                                     <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                         <ImageIcon size={20} className="text-blue-500" /> Visualização Ampliada
                                     </h3>
                                     <div className="flex items-center gap-2">
                                         <button
                                             onClick={() => handleDownloadClick(zoomedPhoto.photo, `photo-zoom-dl-${zoomedPhoto.index}`)}
                                             disabled={downloading === `photo-zoom-dl-${zoomedPhoto.index}`}
                                             className="btn-secondary py-1.5 px-3 flex items-center gap-2"
                                             title="Baixar foto"
                                             aria-label="Baixar foto"
                                         >
                                             {downloading === `photo-zoom-dl-${zoomedPhoto.index}` ? <Loader className="animate-spin" size={16} /> : <Download size={16} />}
                                             <span className="hidden sm:inline">Baixar</span>
                                         </button>
                                         {zoomedPhoto.canDeletePhoto && (
                                             <button
                                                 onClick={() => {
                                                     handleDeleteRequest('photo', { tradeId: trade.id, photo: zoomedPhoto.photo });
                                                     setZoomedPhoto(null);
                                                 }}
                                                 disabled={isProcessing}
                                                 className="py-1.5 px-3 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40 rounded-lg transition-colors flex items-center gap-2"
                                                 title="Excluir foto"
                                                 aria-label="Excluir foto"
                                             >
                                                 <Trash2 size={16} />
                                                 <span className="hidden sm:inline">Excluir</span>
                                             </button>
                                         )}
                                         <button
                                             onClick={() => setZoomedPhoto(null)}
                                             className="p-2 ml-2 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-slate-900"
                                             aria-label="Fechar visualização"
                                             title="Fechar visualização"
                                         >
                                             <X size={20} className="text-slate-600 dark:text-slate-300" />
                                         </button>
                                     </div>
                                 </div>
                                 {/* Body */}
                                 <div className="flex-1 overflow-hidden bg-slate-100 dark:bg-slate-900/50 p-4 flex items-center justify-center cursor-grab active:cursor-grabbing">
                                    <TransformWrapper
                                        initialScale={1}
                                        minScale={0.5}
                                        maxScale={4}
                                        centerOnInit={true}
                                        wheel={{ step: 0.1 }}
                                        pinch={{ step: 5 }}
                                    >
                                        <TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full flex items-center justify-center">
                                            <ImageWithLoader
                                                primarySrc={zoomedPhoto.photo.url}
                                                fallbackSrc={zoomedPhoto.photo.thumbnailURL}
                                                alt={`Foto ampliada`}
                                                className="w-full h-full rounded-lg shadow-inner bg-transparent dark:bg-transparent pointer-events-none"
                                                imgClassName="object-contain pointer-events-none"
                                            />
                                        </TransformComponent>
                                    </TransformWrapper>
                                 </div>
                             </div>
                         )}

                    </div>
                </div>

                {/* --- MOBILE FOOTER SAVE BAR --- */}
                <div className="md:hidden border-t border-slate-200 dark:border-slate-700 p-4 bg-white dark:bg-slate-800 flex items-center justify-between shrink-0">
                    <button onClick={handleClose} className="btn-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">Cancelar</button>
                    {canManagePhotos && (
                        <button onClick={handleSubmit} disabled={isProcessing} className="btn-primary flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
                            {isProcessing ? <Loader className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                            Salvar
                        </button>
                    )}
                </div>

            </div>
        </div>
    );
};

export default TradeModal;
